import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { Box, mq, Pill, Text, theme } from "@pallet-hq/hegel"
import {
    SpaceBetweenVertCenteredRow,
    VerticallyCenteredRow,
} from "components/atoms"
import CircleImagePreview from "components/Image/CircleImagePreview"
import { NewTabLink } from "components/Link/NewTabLink"
import ReadMore from "components/ReadMore"
import { BUTTON_TYPES, TRACK_EVENTS } from "constants/events"
import mixpanel from "mixpanel-browser"
import { CompanyLink } from "page-components/atoms/AppPages"
import { useState } from "react"
import {
    FiDollarSign,
    FiFacebook,
    FiHome,
    FiInstagram,
    FiLinkedin,
    FiThermometer,
    FiTrendingUp,
    FiTwitter,
    FiUsers,
} from "react-icons/fi"
import { getTagColors } from "theme/utils"
import { CompanyPreview_company } from "__generated__/CompanyPreview_company"
import { useScreenSize } from "utils/hooks/useSize"
import { CompanyInfoRow } from "./CompanyInfoRow"

const CompanyPreviewFragment = gql`
    fragment CompanyPreview_company on CompanyType {
        name
        slug
        image
        website
        facebook
        instagram
        linkedin
        twitter
        tagline
        stockSymbol
        totalRaised
        yearFounded
        size {
            name
        }
        investors {
            name
        }
        fundingLevel {
            intuitiveName
            showAmountRaised
        }
    }
`

export type CompanyPreviewProps = {
    company: CompanyPreview_company
}

const CompanyPreview = (props: CompanyPreviewProps) => {
    const { isMobile } = useScreenSize()
    const [isMinimized, setIsMinimized] = useState(true)

    return (
        <CompanyPreviewDiv>
            <Box mb="small">
                <CircleImagePreview {...props.company} />
            </Box>
            <SmallSpacingDiv>
                <SpaceBetweenVertCenteredRow>
                    <VerticallyCenteredRow>
                        <CompanyLink
                            href={props.company.website}
                            onClick={e => {
                                mixpanel.track(TRACK_EVENTS.BUTTON_CLICKED, {
                                    type: BUTTON_TYPES.COMPANY,
                                    company_slug: props.company.slug,
                                    company_name: props.company.name,
                                })
                                e.stopPropagation()
                            }}
                        >
                            <Text variant={["headlineSmall", "headlineMedium"]}>
                                {props.company.name}
                            </Text>
                        </CompanyLink>
                        <SocialIconsDiv>
                            {props.company.facebook && (
                                <StyledCompanySocialLink
                                    href={props.company.facebook}
                                >
                                    <FiFacebook size={24} />
                                </StyledCompanySocialLink>
                            )}
                            {props.company.twitter && (
                                <StyledCompanySocialLink
                                    href={props.company.twitter}
                                >
                                    <FiTwitter size={24} />
                                </StyledCompanySocialLink>
                            )}
                            {props.company.linkedin && (
                                <StyledCompanySocialLink
                                    href={props.company.linkedin}
                                >
                                    <FiLinkedin size={24} />
                                </StyledCompanySocialLink>
                            )}
                            {props.company.instagram && (
                                <StyledCompanySocialLink
                                    href={props.company.instagram}
                                >
                                    <FiInstagram size={24} />
                                </StyledCompanySocialLink>
                            )}
                        </SocialIconsDiv>
                    </VerticallyCenteredRow>
                </SpaceBetweenVertCenteredRow>
            </SmallSpacingDiv>
            <SmallSpacingDiv>
                <Text variant={["paragraphMedium", "paragraphLarge"]}>
                    {props.company.tagline}
                </Text>
            </SmallSpacingDiv>
            {(!isMobile || !isMinimized) && (
                <>
                    <SmallSpacingDiv>
                        {props.company.size && (
                            <CompanyInfoRow
                                title="Size"
                                value={props.company.size?.name + " employees"}
                                icon={FiUsers}
                            />
                        )}
                    </SmallSpacingDiv>
                    {props.company.fundingLevel && (
                        <>
                            <SmallSpacingDiv>
                                <CompanyInfoRow
                                    title="Funding Level"
                                    value={
                                        props.company.fundingLevel.intuitiveName
                                    }
                                    icon={FiThermometer}
                                />
                            </SmallSpacingDiv>

                            {props.company.fundingLevel.intuitiveName ===
                            "Public" ? (
                                <SmallSpacingDiv>
                                    <CompanyInfoRow
                                        title="Symbol"
                                        value={
                                            props.company.stockSymbol.split(
                                                ":"
                                            )[1] || ""
                                        }
                                        icon={FiTrendingUp}
                                    />
                                </SmallSpacingDiv>
                            ) : (
                                props.company.totalRaised && (
                                    <SmallSpacingDiv>
                                        <CompanyInfoRow
                                            title="Total Amount Raised"
                                            value={`$${parseFloat(
                                                props.company.totalRaised
                                            ).toFixed(1)} M`}
                                            icon={FiDollarSign}
                                        />
                                    </SmallSpacingDiv>
                                )
                            )}
                        </>
                    )}
                    {props.company.yearFounded && (
                        <SmallSpacingDiv>
                            <CompanyInfoRow
                                title="Year Founded"
                                value={props.company.yearFounded.toString()}
                                icon={FiHome}
                            />
                        </SmallSpacingDiv>
                    )}
                    {props.company.investors.length !== 0 && (
                        <>
                            <Text variant="uiSmall600">Investors</Text>
                            <TagDiv>
                                {props.company.investors.map((investor, i) => (
                                    <Pill
                                        key={i}
                                        backgroundColor="transparent"
                                        borderColor={getTagColors(
                                            investor.name
                                        )}
                                        color={getTagColors(investor.name)}
                                    >
                                        {investor.name}
                                    </Pill>
                                ))}
                            </TagDiv>
                        </>
                    )}
                </>
            )}
            {isMobile && (
                <ReadMore
                    isMinimized={isMinimized}
                    setIsMinimized={setIsMinimized}
                />
            )}
        </CompanyPreviewDiv>
    )
}

CompanyPreview.fragment = CompanyPreviewFragment

const SmallSpacingDiv = styled.div`
    ${mq({ marginBottom: [theme.space.small, theme.space.small] })}
`

const CompanyPreviewDiv = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.gray100};
    height: 100%;
    padding: ${theme.space.large};
    ${mq({ padding: [theme.space.small, theme.space.large] })}
`

const TagDiv = styled.div`
    display: flex;
    flex-flow: row wrap;
    margin-top: ${theme.space.xsmall};

    & > div {
        margin-right: ${theme.space.small};
        margin-bottom: ${theme.space.xxsmall};
    }
`

const StyledCompanySocialLink = styled(NewTabLink)`
    margin-left: ${theme.space.xsmall};

    & > svg {
        stroke: ${theme.colors.blue};
        width: 18px;
        height: 18px;
    }

    display: inline-block;
    height: 18px;
`

const SocialIconsDiv = styled.div`
    margin-left: ${theme.space.small};
    display: flex;
    align-items: center;
`
export default CompanyPreview
